/*
  <quick-buy-dialog>: buy a product from its card without leaving the page.

  One element per page (snippets/quick-buy-dialog.liquid). A press on any
  [data-quick-buy] button opens the dialog, fetches sections/quick-buy.liquid
  from that button's product URL and places it inside. The fetched markup is a
  regular product form, so assets/product-form.js drives it exactly as it does
  on the product page. Stylesheets and scripts named by the fetched markup are
  added to the document once, because markup inserted by script does not load
  its own scripts.

  The dialog closes on Escape, on a press outside its box, on its close button
  and after a successful add to cart, so the cart drawer can open over the page.
*/
if (!customElements.get('quick-buy-dialog')) {
  const absolute = (url) => {
    try {
      return new URL(url, window.location.href);
    } catch (error) {
      return null;
    }
  };

  /* The same asset can be referenced with and without a version query; the path identifies it. */
  const assetKey = (url) => {
    const parsed = absolute(url);
    return parsed ? `${parsed.host}${parsed.pathname}` : '';
  };

  const scriptLoads = new Map();

  /* Adds a script to the page unless it is already there. Resolves when it has run (or failed). */
  const loadScript = (src) => {
    const key = assetKey(src);
    if (!key) return Promise.resolve();
    if (scriptLoads.has(key)) return scriptLoads.get(key);

    const present = Array.from(document.scripts).some((script) => script.src && assetKey(script.src) === key);
    const load = present
      ? Promise.resolve()
      : new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = src;
          script.addEventListener('load', resolve);
          script.addEventListener('error', resolve);
          document.head.append(script);
        });
    scriptLoads.set(key, load);
    return load;
  };

  /* Adds a stylesheet to the page unless it is already there. Resolves when it has applied (or failed). */
  const loadStylesheet = (href) => {
    const key = assetKey(href);
    if (!key) return Promise.resolve();
    const present = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some(
      (link) => assetKey(link.getAttribute('href')) === key
    );
    if (present) return Promise.resolve();

    return new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.addEventListener('load', resolve);
      link.addEventListener('error', resolve);
      document.head.append(link);
    });
  };

  class QuickBuyDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.body = this.querySelector('[data-quick-buy-body]');
      this.status = this.querySelector('[data-quick-buy-status]');
      this.loading = this.querySelector('[data-quick-buy-loading]');
      if (!this.dialog || !this.body || typeof this.dialog.showModal !== 'function') return;

      if (!this.bound) {
        this.bound = true;
        this.isOpen = false;
        this.dialog.addEventListener('close', () => this.afterClose());
        this.dialog.addEventListener('pointerdown', (event) => {
          this.pressStartedOutside = this.isOutside(event);
        });
        this.dialog.addEventListener('click', (event) => this.onDialogClick(event));
      }

      /* Cards come and go (filters, recommendations, search), so presses are caught at the document. */
      this.onDocumentClick = this.onDocumentClick || ((event) => this.onTriggerClick(event));
      document.addEventListener('click', this.onDocumentClick);
      if (this.stopListening) this.stopListening();
      this.stopListening = Theme.on('cart:updated', (event) => {
        const detail = event.detail || {};
        if (this.isOpen && detail.source === 'product-form') this.close();
      });
    }

    disconnectedCallback() {
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
      if (this.stopListening) this.stopListening();
      this.stopListening = null;
      if (this.isOpen) this.close();
    }

    onTriggerClick(event) {
      const target = event.target;
      const trigger = target && typeof target.closest === 'function' ? target.closest('[data-quick-buy]') : null;
      if (!trigger || !trigger.dataset.productUrl) return;
      event.preventDefault();
      this.open(trigger);
    }

    /* True when the pointer is on the backdrop rather than inside the dialog box. */
    isOutside(event) {
      if (event.target !== this.dialog) return false;
      const box = this.dialog.getBoundingClientRect();
      return (
        event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom
      );
    }

    onDialogClick(event) {
      if (event.target.closest('[data-quick-buy-close]')) {
        this.close();
        return;
      }
      /* A drag that starts on the form and ends on the backdrop is not a request to close */
      const startedOutside = this.pressStartedOutside;
      this.pressStartedOutside = false;
      if (startedOutside && this.isOutside(event)) this.close();
    }

    async open(trigger) {
      const url = trigger.dataset.productUrl;
      if (this.request) this.request.abort();
      const request = new AbortController();
      this.request = request;
      this.trigger = trigger;

      this.reset();
      this.setBusy(true);
      this.announce(this.dataset.loadingText);

      if (!this.dialog.open) this.dialog.showModal();
      this.isOpen = true;
      Theme.lockScroll();

      try {
        const html = await Theme.fetchSection(url, this.dataset.sectionId || 'quick-buy', { signal: request.signal });
        if (request.signal.aborted) return;

        const page = new DOMParser().parseFromString(html, 'text/html');
        const content = page.querySelector('[data-quick-buy-content]');
        if (!content) throw new Error('Quick buy section is empty');

        const stylesheets = Array.from(page.querySelectorAll('link[rel="stylesheet"][href]'));
        const scripts = Array.from(page.querySelectorAll('script[src]'));
        const hrefs = stylesheets.map((link) => link.getAttribute('href'));
        const sources = scripts.map((script) => script.getAttribute('src'));
        stylesheets.concat(scripts).forEach((node) => node.remove());

        /* Styles first, so the product does not appear unstyled */
        await Promise.all(hrefs.map(loadStylesheet));
        if (request.signal.aborted) return;

        this.body.replaceChildren(content);
        const title = content.dataset.titleId ? document.getElementById(content.dataset.titleId) : null;
        if (title && content.contains(title)) this.dialog.setAttribute('aria-labelledby', title.id);

        await Promise.all(sources.map(loadScript));
        if (request.signal.aborted) return;

        this.setBusy(false);
        this.announce(content.dataset.loadedText || '');
      } catch (error) {
        if (error.name === 'AbortError' || request.signal.aborted) return;
        /* The product page always works, so a failed request sends the shopper there */
        this.setBusy(false);
        this.announce(this.dataset.errorText);
        window.location.assign(url);
      }
    }

    close() {
      this.afterClose();
      if (this.dialog.open) this.dialog.close();
    }

    /*
      Runs straight away when the theme closes the dialog and from the native
      "close" event when the browser does (Escape). The native event arrives
      later, after the cart drawer may already have opened, so it must not
      undo anything a second time.
    */
    afterClose() {
      if (!this.isOpen) return;
      this.isOpen = false;
      if (this.request) this.request.abort();
      this.request = null;
      this.pressStartedOutside = false;

      const trigger = this.trigger;
      this.trigger = null;

      this.reset();
      this.setBusy(false);
      Theme.unlockScroll();

      if (this.dialog.open) this.dialog.close();
      if (trigger && trigger.isConnected && typeof trigger.focus === 'function') trigger.focus();
    }

    reset() {
      this.body.replaceChildren();
      this.dialog.removeAttribute('aria-labelledby');
      this.announce('');
    }

    setBusy(busy) {
      if (busy) this.body.setAttribute('aria-busy', 'true');
      else this.body.removeAttribute('aria-busy');
      if (this.loading) this.loading.hidden = !busy;
    }

    announce(message) {
      if (this.status) this.status.textContent = message || '';
    }
  }

  customElements.define('quick-buy-dialog', QuickBuyDialog);
}
